// interfaces/types
import type { DashMiddleware, DashStore, DashNext } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { CommonOrgDealerVehicle, Bookouts } from '@makemydeal/dr-shared-types/vehicleTypes';
import type { IVehicle, BookType } from '@makemydeal/dr-platform-types';
import { DcVppVehicleUsage } from '@makemydeal/dr-platform-types';
import type { BaggageEntry, VehicleBffData } from '@makemydeal/dr-dash-bff-types';

import { FETCH_CVI_VEHICLE_SUCCESS } from '../actionTypes/vehicleActionTypes';
import { DEAL_COMPARISON_BAGGAGE_NAME } from '../constants';
import { updateCviPricing } from '../actions/vehicleActionCreators';
import { updateVppSurchargesAttributes } from '../actions/vehicleProtectionActionCreators';
import { getVppSurchargesOverrides } from '../selectors/vehicleProtectionSelectors';

import { actionCreators as externalOfferReduxActionCreators, actions as offerReduxActionTypes } from '@makemydeal/dr-offer-redux';
import { featureToggleSelectors, dealerSelectors, initActionTypes } from '@makemydeal/dr-shared-store';

import { cviToVehicle } from '@makemydeal/dr-shared-ui-utils/mapper/cviToVehicle';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import * as vehicleSelectors from '../selectors/vehicleSelectors';
import * as unsavedChangesSelectors from '../selectors/unsavedChangesSelectors';
import type { DealerPreferredBookSource } from '../types/vehicleTypes';

const getVehicleFromCVIResponse = (action: AnyFSA): CommonOrgDealerVehicle | null => {
    if (!Array.isArray(action.payload) || action.payload.length === 0) {
        return null;
    }

    const vehicles = action.payload as CommonOrgDealerVehicle[];
    const activeVehicle = vehicles.find((vehicle) => vehicle.inventoryStatus === 'ACTIVE');

    return activeVehicle || vehicles[0];
};

const canRebaselineCviEnrichment = (state: StateTree): boolean =>
    featureToggleSelectors.isCviLoadRebaselineEnabled(state) && !unsavedChangesSelectors.currentOfferHasUnsavedChanges(state);

const applyInServiceDate = (store: DashStore, vehicleData: IVehicle, rebaselineUnsavedChanges: boolean) => {
    const state = store.getState();

    if (!vehicleData.inServiceDate) {
        return;
    }

    const currentSurcharges = getVppSurchargesOverrides(state);
    if (currentSurcharges.inServiceDate) {
        return;
    }

    const updatedSurcharges = {
        ...currentSurcharges,
        inServiceDate: vehicleData.inServiceDate
    };

    const surchargesAction = updateVppSurchargesAttributes(updatedSurcharges);
    store.dispatch(rebaselineUnsavedChanges ? { ...surchargesAction, meta: { rebaselineUnsavedChanges: true } } : surchargesAction);
};

const getDealerPreferredBookSource = (
    state: Parameters<typeof dealerSelectors.getDealerPreferredBookSource>[0],
    bookouts?: Bookouts
): DealerPreferredBookSource | undefined => {
    const preferredBookSource = dealerSelectors.getDealerPreferredBookSource(state);
    const preferredBookoutType = dealerSelectors.getDealerPreferredBookoutType(state);
    const isEnableDealerPreferredBookSource = featureToggleSelectors.isEnableDealerPreferredBookSource(state);

    if (!isEnableDealerPreferredBookSource || !preferredBookSource) {
        return undefined;
    }

    return {
        wholesaleBookSource: preferredBookSource,
        wholesaleValueType: preferredBookoutType || undefined,
        wholesaleValue: bookouts?.[preferredBookSource as BookType]?.bookoutValue
    };
};

const applyDealerPreferredBookSource = (store: DashStore, vehicle: Partial<IVehicle>) => {
    const state = store.getState();

    if (vehicle.wholesaleBookSource || vehicleSelectors.getIsQuoteWithoutVehicle(state)) {
        return;
    }

    const preferred = getDealerPreferredBookSource(state);
    if (!preferred) {
        return;
    }

    store.dispatch(
        externalOfferReduxActionCreators.updateVehicleInfoDetails(
            {
                vehicle: {
                    wholesaleBookSource: preferred.wholesaleBookSource,
                    wholesaleValueType: preferred.wholesaleValueType
                }
            },
            false
        )
    );
};

const applyDealerPreferredBookSourceToSwitchVehicle = (store: DashStore, action: AnyFSA) => {
    const vehicle = action.payload;
    if (!vehicle) {
        return;
    }

    const preferred = getDealerPreferredBookSource(store.getState(), vehicle.bookouts);
    if (!preferred) {
        return;
    }

    action.payload = { ...vehicle, ...preferred };
};

const hasManagerViewVersion = (baggage?: BaggageEntry[]): boolean => {
    const comparisonBaggage = Array.isArray(baggage)
        ? baggage.find(({ name }) => name === DEAL_COMPARISON_BAGGAGE_NAME)
        : undefined;
    return !!comparisonBaggage?.value?.content?.length;
};

const applyDefaultVehicleUse = (store: DashStore, action: AnyFSA) => {
    const state = store.getState();

    if (
        !scopedStateSelectors.isCurrentScopePrimary(state as any) ||
        hasManagerViewVersion(action.payload.baggage) ||
        vehicleSelectors.getVehicleUse(state) ||
        vehicleSelectors.getIsQuoteWithoutVehicle(state)
    ) {
        return;
    }

    const vehicle: Partial<VehicleBffData> = { vehicleUse: DcVppVehicleUsage.PERSONAL };
    store.dispatch(
        externalOfferReduxActionCreators.updateVehicleInfoDetails(
            { vehicle } as externalOfferReduxActionCreators.vehicleInfoPayload,
            false
        )
    );
};

const applyDefaultVehicleUseToSwitchVehicle = (store: DashStore, action: AnyFSA) => {
    const vehicle = action.payload;
    if (!vehicle || vehicle.vehicleUse) {
        return;
    }

    action.payload = { ...vehicle, vehicleUse: DcVppVehicleUsage.PERSONAL };
};

const updateVehicleInfo = (store: DashStore, vehicleData: Partial<IVehicle>, canRebaseline: boolean): boolean => {
    const state = store.getState();
    const currentVehicle = vehicleSelectors.getVehicle(state);
    const isTrimAndStockEditEnabled = featureToggleSelectors.isTrimAndStockEditEnabled(state);

    const vehicleUpdate: Partial<IVehicle> = {
        destinationCharge: currentVehicle.destinationCharge ?? vehicleData.destinationCharge,
        inventoryDate: vehicleData.inventoryDate,
        fuel: currentVehicle.fuel ?? vehicleData.fuel,
        driveLine: currentVehicle.driveLine ?? vehicleData.driveLine,
        notes: vehicleData.notes,
        location: vehicleData.location,
        chromeStyleId: currentVehicle.chromeStyleId || vehicleData.chromeStyleId,
        cityFuelEconomy: vehicleData.cityFuelEconomy,
        highwayFuelEconomy: vehicleData.highwayFuelEconomy,
        imageUrl: currentVehicle.imageUrl || vehicleData.imageUrl,
        // Vehicle fields for optimized save offer (US1792865)
        engine: currentVehicle.engine ?? vehicleData.engine,
        transmission: currentVehicle.transmission ?? vehicleData.transmission,
        bodyStyle: currentVehicle.bodyStyle ?? vehicleData.bodyStyle,
        cost: currentVehicle.cost || vehicleData.cost,
        invoiceAmount: currentVehicle.invoiceAmount || vehicleData.invoiceAmount,
        bookouts: vehicleData.bookouts,
        grossVehicleWeightRating: currentVehicle.grossVehicleWeightRating ?? vehicleData.grossVehicleWeightRating
    };

    if (isTrimAndStockEditEnabled) {
        vehicleUpdate.trim = currentVehicle.trim || vehicleData.trim;
    }

    if (!currentVehicle.wholesaleBookSource) {
        const preferred = getDealerPreferredBookSource(state, vehicleData.bookouts);
        if (preferred) {
            vehicleUpdate.wholesaleBookSource = preferred.wholesaleBookSource;
            vehicleUpdate.wholesaleValueType = preferred.wholesaleValueType;
            vehicleUpdate.wholesaleValue = preferred.wholesaleValue;
        } else {
            vehicleUpdate.wholesaleBookSource = vehicleData.wholesaleBookSource;
            vehicleUpdate.wholesaleValue = vehicleData.wholesaleValue;
        }
    }

    const hasInvoiceAmountChanged = vehicleUpdate.invoiceAmount !== currentVehicle.invoiceAmount;
    const hasCostChanged = vehicleUpdate.cost !== currentVehicle.cost;

    const calculatePayment = hasInvoiceAmountChanged || hasCostChanged;

    const rebaselineUnsavedChanges = canRebaseline && !calculatePayment;

    store.dispatch(
        externalOfferReduxActionCreators.updateVehicleInfoDetails(
            { vehicle: vehicleUpdate },
            calculatePayment,
            rebaselineUnsavedChanges
        )
    );

    if (vehicleData.cost || vehicleData.invoiceAmount) {
        store.dispatch(
            updateCviPricing({
                cviCost: vehicleData.cost,
                dealCostAtCviFetch: currentVehicle.cost,
                cviInvoiceAmount: vehicleData.invoiceAmount,
                dealInvoiceAtCviFetch: currentVehicle.invoiceAmount
            })
        );
    }

    return rebaselineUnsavedChanges;
};

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    if (action.type === offerReduxActionTypes.SWITCH_VEHICLE) {
        applyDealerPreferredBookSourceToSwitchVehicle(store, action);
        applyDefaultVehicleUseToSwitchVehicle(store, action);
    }

    const canRebaselineCvi = action.type === FETCH_CVI_VEHICLE_SUCCESS && canRebaselineCviEnrichment(store.getState());

    next(action);
    switch (action.type) {
        case FETCH_CVI_VEHICLE_SUCCESS: {
            const cviVehicle = getVehicleFromCVIResponse(action);
            if (!cviVehicle) {
                break;
            }
            const vehicleData: IVehicle = cviToVehicle(cviVehicle);
            const vehicleUpdateRebaselined = updateVehicleInfo(store, vehicleData, canRebaselineCvi);
            applyInServiceDate(store, vehicleData, vehicleUpdateRebaselined);
            break;
        }
        case initActionTypes.INIT_PENCIL_SUCCESS: {
            applyDealerPreferredBookSource(store, action.payload.vehicle);
            applyDefaultVehicleUse(store, action);
            break;
        }
        default:
            break;
    }
};
